import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Scale, Eye, EyeOff, Shield, AlertTriangle } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import Skeleton from "@/components/jea/Skeleton";

const publicClient = axios.create({
  baseURL: (process.env.REACT_APP_BACKEND_URL || "") + "/api",
});

const InviteAcceptPage = () => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, invalid: null, invite: null });
  const [form, setForm] = useState({ full_name: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setState({ loading: false, invalid: "Missing invite token.", invite: null }); return; }
      try {
        const { data } = await publicClient.get("/team/invites/lookup", { params: { token } });
        setState({ loading: false, invalid: null, invite: data });
      } catch (e) {
        const detail = e?.response?.data?.detail || "This invite is no longer valid.";
        setState({ loading: false, invalid: detail, invite: null });
      }
    })();
  }, [token]);

  const valid = form.full_name.trim() &&
                form.password.length >= 8 &&
                /\d/.test(form.password) &&
                /[A-Za-z]/.test(form.password) &&
                form.password === form.confirm;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await publicClient.post("/team/invites/accept", {
        token, password: form.password, full_name: form.full_name.trim(),
      });
      toast.success("Invite accepted. Verify with 2FA to continue.");
      // TwoFactorPage reads pending_token from router state (see LoginPage
      // + RegisterPage for the same pattern).
      nav("/2fa", {
        replace: true,
        state: { pending_token: data.pending_token, email: data.email },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept invite.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-bg-primary px-4" data-testid="invite-accept-page">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-[440px]"
      >
        <JeaCard className="p-12 shadow-elevated">
          <div className="flex items-center gap-2 justify-center mb-3">
            <Scale className="text-accent-pink" size={22} />
            <span className="text-accent-pink font-extrabold text-[18px]">Judgment Enforcement AI</span>
          </div>
          <div className="h-px bg-accent-pink/20 mb-6" />

          {state.loading ? (
            <Skeleton className="h-40" />
          ) : state.invalid ? (
            <div className="text-center" data-testid="invite-invalid">
              <div className="w-12 h-12 rounded-full bg-danger/15 border border-danger/40 flex items-center justify-center mx-auto mb-3">
                <AlertTriangle size={20} className="text-danger" />
              </div>
              <h2 className="text-white font-bold text-[18px]">This invite is no longer valid.</h2>
              <p className="text-text-secondary text-[13px] mt-2">{state.invalid}</p>
              <Link to="/login" className="mt-4 inline-block text-accent-pink text-[13px] font-semibold hover:text-accent-pink-light">
                Go to Sign In →
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-white font-extrabold text-[22px]" data-testid="invite-accept-heading">
                Accept Invite
              </h2>
              <p className="text-text-secondary text-[14px] mt-2">
                <span className="text-white font-semibold">{state.invite.parent_account_name}</span> invited you to
                join their team as a <span className="text-accent-pink font-semibold">{state.invite.role}</span>.
              </p>
              <div className="mt-6 space-y-3">
                <div>
                  <div className="jea-caption mb-1.5">Email</div>
                  <JeaInput value={state.invite.email} disabled data-testid="invite-email-readonly" />
                </div>
                <div>
                  <div className="jea-caption mb-1.5">Full Name</div>
                  <JeaInput
                    value={form.full_name}
                    onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                    placeholder="Your full name"
                    data-testid="invite-full-name"
                  />
                </div>
                <div>
                  <div className="jea-caption mb-1.5">Password</div>
                  <div className="relative">
                    <JeaInput
                      type={showPw ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                      data-testid="invite-password"
                    />
                    <button type="button" onClick={() => setShowPw((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-accent-pink"
                            data-testid="invite-password-toggle">
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <div className="text-text-muted text-[11px] mt-1">
                    Min 8 chars, must contain a letter + a number.
                  </div>
                </div>
                <div>
                  <div className="jea-caption mb-1.5">Confirm Password</div>
                  <JeaInput
                    type={showPw ? "text" : "password"}
                    value={form.confirm}
                    onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                    data-testid="invite-confirm"
                  />
                  {form.confirm.length > 0 && form.confirm !== form.password && (
                    <div className="text-danger text-[11px] mt-1">Passwords don't match.</div>
                  )}
                </div>
              </div>
              <JeaButton fullWidth size="lg" onClick={submit} disabled={!valid} loading={busy}
                         className="mt-5" data-testid="invite-accept-submit">
                Accept Invite
              </JeaButton>
              <p className="text-text-muted text-[11px] text-center mt-4">
                Your invite expires on {new Date(state.invite.expires_at).toLocaleString()}.
              </p>
            </>
          )}
        </JeaCard>
      </motion.div>
    </div>
  );
};

export default InviteAcceptPage;
